import React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  IconButton,
  Modal,
  Button,
  TextField,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddCircleOutlineOutlined from "@mui/icons-material/AddCircleOutlineOutlined";
import { 
  getCompanyNews, 
  addSingleCompanyNews, 
  deleteSingleNewsById 
} from "./api/underwritingApi";

export default function SimpleNews({ newsData = [], tenantId, companyId }) {
  const [items, setItems] = React.useState(newsData || []);
  const [openAddModal, setOpenAddModal] = React.useState(false);
  const [newUrl, setNewUrl] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    setItems(newsData || []);
  }, [newsData]);

  const handleOpenAddModal = () => setOpenAddModal(true);
  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    setNewUrl("");
  };

  const refreshNews = async () => {
    if (!tenantId || !companyId) return;
    try {
      const refreshed = await getCompanyNews(tenantId, companyId);
      const extractItems = (resp) => {
        if (!resp) return [];
        if (Array.isArray(resp)) return resp;
        if (Array.isArray(resp?.data)) return resp.data;
        if (Array.isArray(resp?.news)) return resp.news;
        if (Array.isArray(resp?.items)) return resp.items;
        if (Array.isArray(resp?.data?.news)) return resp.data.news;
        if (Array.isArray(resp?.data?.items)) return resp.data.items;
        if (Array.isArray(resp?.news?.items)) return resp.news.items;
        if (Array.isArray(resp?.data?.news?.items)) return resp.data.news.items;
        return [];
      };
      setItems(extractItems(refreshed));
    } catch (e) {
      // fail silently to avoid UX interruption
    }
  };

  const handleAddNews = async () => {
    if (!tenantId || !companyId || !newUrl?.trim()) return;
    try {
      setIsSubmitting(true);
      await addSingleCompanyNews(tenantId, companyId, { url: newUrl.trim() });
      handleCloseAddModal();
      await refreshNews();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    // Optimistic update
    setItems((prev) => prev.filter((it) => it?.id !== id));
    try {
      await deleteSingleNewsById(id);
      // Re-fetch to ensure consistency
      await refreshNews();
    } catch (e) {
      // On error, revert by refreshing from server
      await refreshNews();
    }
  };
  const CustomIcon = () => (
    <Box
      sx={{
        width: 30,
        height: 30,
        backgroundColor: '#FFFFFF',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.29)',
      }}
    >
      <ExpandMoreIcon style={{ color: "#5186EC" }} />
    </Box>
  );

  function extractKeywordsFromUrl(url) {
    const keywordMatches = url.match(
      /\/(materials|material|news|articles|article|blog|blogs)\//
    );

    if (keywordMatches && keywordMatches[1]) {
      if (
        keywordMatches[1] === "materials" ||
        keywordMatches[1] === "material"
      ) {
        return "Materials";
      } else if (keywordMatches[1] === "news") {
        return "News";
      } else if (
        keywordMatches[1] === "article" ||
        keywordMatches[1] === "articles"
      ) {
        return "Articles";
      } else if (
        keywordMatches[1] === "blog" ||
        keywordMatches[1] === "blogs"
      ) {
        return "Blogs";
      }
    } else {
      return ""; 
    }
  }

  return (
    <div style={{ paddingLeft: "25px", paddingRight: "25px", paddingBottom: "25px" }}>
      <Accordion
        width="90%"
        defaultExpanded
        sx={{
          background: "#FAFAFC",
          boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029",
          borderRadius: "8px",
          opacity: "1",
        }}
      >
        <AccordionSummary
          expandIcon={<CustomIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography
            style={{
              width: "290px",
              height: "24px",
              fontSize: "20px",
              fontWeight: "500",
              color: "#1A2A56",
            }}
          >
            News ({items.length})
          </Typography>
          <Box sx={{ ml: "auto" }}>
            <IconButton aria-label="add-news" onClick={handleOpenAddModal} size="small">
              <AddCircleOutlineOutlined sx={{ color: "#ebeced" }} />
            </IconButton>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            {items.map((row, index) => (
              <ListItem key={row.id || index} sx={{ 
                  marginBottom: "10px", 
                  paddingBottom: "10px", 
                  borderBottom: index < items.length - 1 ? "1px solid #E9E9E9" : "none" 
                }}
                secondaryAction={
                  row?.id ? (
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(row.id)} size="small">
                      <DeleteOutlinedIcon sx={{ color: "#ebeced" }} />
                    </IconButton>
                  ) : null
                }
              >
                <ListItemText>
                  <Tooltip title={row.link} arrow>
                    <Typography
                      component="a"
                      href={row.link}
                      target="_blank"
                      sx={{ 
                        cursor: "pointer",
                        textDecoration: "underline",
                        color: "#5186EC",
                        fontSize: "16px",
                        fontWeight: "500",
                        fontFamily: "Rubik, sans-serif",
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      {row.title}
                    </Typography>
                  </Tooltip>
                  {row.snippet && (
                    <Typography
                      style={{
                        textAlign: "left",
                        fontFamily: "Rubik, sans-serif",
                        letterSpacing: "0px",
                        paddingBottom: "10px",
                        color: "#777A82"
                      }}
                    >
                      {row.snippet}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", gap: "15px", fontSize: "12px", color: "#999" }}>
                    {(row.date || row.createdAt) && (
                      <Typography sx={{ fontSize: "12px", color: "#999" }}>
                        {row.date || new Date(row.createdAt).toLocaleDateString()}
                      </Typography>
                    )}
                    {row.source && (
                      <Typography sx={{ fontSize: "12px", color: "#999" }}>
                        Source: {row.source}
                      </Typography>
                    )}
                  </Box>
                </ListItemText>
              </ListItem>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>

      <Modal open={openAddModal} onClose={handleCloseAddModal}>
        <Box sx={{ width: '300px', padding: '20px', margin: '100px auto', background: '#FAFAFC', boxShadow: 'inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029', borderRadius: '8px', opacity: '1' }}>
          <TextField
            label="News URL"
            variant="outlined"
            fullWidth
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
            <Button onClick={handleCloseAddModal}>Cancel</Button>
            <Button onClick={handleAddNews} disabled={isSubmitting || !tenantId || !companyId || !newUrl?.trim()} color="primary">Add</Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
} 